import { useState, useRef, useEffect, type FormEvent } from "react";
import { X, Users, AlertCircle, Loader2, Copy, Check } from "lucide-react";
import { Button } from "./Button";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useSquads } from "../hooks/useSquads";
import { useSquad } from "../contexts/SquadContext";
import { useToast } from "../contexts/ToastContext";
import type { Database } from "../lib/database.types";

type Squad = Database["public"]["Tables"]["squads"]["Row"];

interface CreateSquadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (squadCode: string) => void;
}

export function CreateSquadModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateSquadModalProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { createSquad, getOwnedSquad, renameSquad } = useSquads();
  const { refetch: refetchSquadContext } = useSquad();
  const { showToast } = useToast();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingOwnership, setCheckingOwnership] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownedSquad, setOwnedSquad] = useState<Squad | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [squadCode, setSquadCode] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if user owns a squad when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setCheckingOwnership(true);
      getOwnedSquad()
        .then((squad) => {
          setOwnedSquad(squad);
          if (squad) {
            setName(squad.name);
            setSquadCode(squad.code);
          }
        })
        .catch((err) => {
          console.error("Failed to check owned squad:", err);
        })
        .finally(() => {
          setCheckingOwnership(false);
        });
    } else if (isOpen && !user) {
      setOwnedSquad(null);
      setName("");
      setSquadCode("");
    }
  }, [isOpen, user, getOwnedSquad]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!user) {
      setError(t("common.loginRequired"));
      setLoading(false);
      return;
    }

    if (!name.trim()) {
      setError(t("common.leagueNameRequired"));
      setLoading(false);
      return;
    }

    try {
      let result;
      
      // If user owns a squad, rename it. Otherwise, create a new one.
      if (ownedSquad) {
        result = await renameSquad(ownedSquad.id, name.trim());
        
        if (result.error) {
          setError(result.error.message || t("common.failedRenameLeague"));
          setLoading(false);
          return;
        }

        const squadData = result.data;
        if (squadData && "code" in squadData) {
          setLoading(false);
          setSquadCode((squadData as Squad).code);
          showToast(t("squad.renameSuccess"), "success");

          // Refetch SquadContext to update the UI
          refetchSquadContext();

          // Update owned squad to reflect the new name
          setOwnedSquad(squadData as Squad);
        }
      } else {
        result = await createSquad(name.trim());

        if (result.error) {
          setError(result.error.message || t("common.failedCreateLeague"));
          setLoading(false);
          return;
        }

        const squadData = result.data;
        if (squadData && "code" in squadData) {
          setLoading(false);
          setSquadCode((squadData as Squad).code);
          setShowSuccess(true);
          showToast(t("squad.createSuccess"), "success");
          
          // Call onSuccess callback and close modal after a short delay
          timeoutRef.current = setTimeout(() => {
            if (onSuccess) {
              onSuccess((squadData as Squad).code);
            }
            handleClose();
          }, 1500);
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("common.unexpectedError");
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setError(null);
    setOwnedSquad(null);
    setShowSuccess(false);
    setSquadCode("");
    setCopied(false);
    onClose();
  };

  const handleCopyCode = async () => {
    if (!squadCode) return;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(squadCode);
        setCopied(true);
        showToast(t("leaderboard.copied"), "success");
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback method
        const input = document.createElement("input");
        input.value = squadCode;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setCopied(true);
        showToast(t("leaderboard.copied"), "success");
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white ">
          <div className="flex items-center justify-between mb-2 ">
            <h2 className="text-2xl font-bold">
              {ownedSquad 
                ? t("league.rename.title")
                : t("league.create.title")
              }
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/90 text-sm">
            {ownedSquad
              ? t("league.rename.subtitle")
              : t("league.create.subtitle")
            }
          </p>
        </div>

        {/* Content */}
        {showSuccess && !ownedSquad ? (
          <div className="p-6 space-y-4">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">
                  {t("league.create.success")}
                </h3>
                <p className="text-sm text-slate-600">
                  {t("league.create.successMessage")}
                </p>
              </div>
              
              {/* Squad Code */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide">
                  {t("league.create.inviteCode")}
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white border-2 border-primary rounded-lg px-4 py-3 text-center">
                    <span className="text-2xl font-bold text-primary tracking-wider">
                      {squadCode}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className="px-4 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-smooth flex items-center gap-2 font-semibold"
                  >
                    {copied ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>{t("leaderboard.copied")}</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5" />
                        <span>{t("leaderboard.copy")}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="button"
                variant="primary"
                className="w-full !py-3"
                onClick={() => {
                  if (onSuccess) {
                    onSuccess(squadCode);
                  }
                  handleClose();
                }}
              >
                {t("league.create.done")}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {checkingOwnership ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : (
              <>
                <div className="space-y-2 ">
                  <label className="block text-sm font-medium text-slate-900">
                    {ownedSquad
                      ? t("league.rename.nameLabel")
                      : t("league.create.nameLabel")
                    }
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={
                        ownedSquad
                          ? t("league.rename.namePlaceholder")
                          : t("league.create.namePlaceholder")
                      }
                      required
                      maxLength={50}
                      className="w-full border border-slate-200 rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-white"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    {ownedSquad
                      ? t("league.rename.nameHint")
                      : t("league.create.nameHint")
                    }
                  </p>
                </div>

                {/* Show squad code when renaming */}
                {ownedSquad && squadCode && (
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-600 font-medium flex-shrink-0">
                      {t("league.create.inviteCode")}:
                    </span>
                    <span className="text-lg font-bold text-primary tracking-wider flex-1 text-center">
                      {squadCode}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyCode}
                      className="p-1.5 hover:bg-slate-200 rounded transition-smooth flex-shrink-0"
                      title={copied ? t("leaderboard.copied") : t("leaderboard.copy")}
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-600" />
                      )}
                    </button>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-700/20 rounded-xl text-red-700 text-sm ">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {!user && (
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-400/20 rounded-xl text-primary-dark text-sm ">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{t("league.create.loginRequired")}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1 !py-3"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    {t("league.create.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="flex-1 !py-3"
                    disabled={loading || !user}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>
                          {ownedSquad
                            ? t("league.rename.renaming")
                            : t("league.create.creating")
                          }
                        </span>
                      </div>
                    ) : (
                      ownedSquad
                        ? t("league.rename.rename")
                        : t("league.create.create")
                    )}
                  </Button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
